/**
 * The workspace: navigation, server state and the flows that span screens.
 *
 * State lives here rather than in the screens because most of it is shared -
 * the document list feeds the grid, the comparison and the status bar, and an
 * analysis started from the top bar has to land in whichever screen is open.
 * Screens stay presentational and take what they need as props.
 *
 * There is no sign-in step: the app opens straight onto one fixed library, so
 * this component mounts once and lives for the tab.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiError, api } from "./api";
import type { DocumentDetail as Detail, DocumentSummary, Health } from "./api";
import { Button, IconButton } from "./components/primitives";
import { SideNav, StatusBar, TopBar } from "./components/shell";
import type { NavItem, ScreenId } from "./components/shell";
import { Toast } from "./components/overlays";
import { PromoBar } from "./components/PromoBar";
import { markVisit, shouldShowWelcome } from "./demo";
import { Contact } from "./screens/Contact";
import { Feedback } from "./screens/Feedback";
import { HowTo, VideoFrame } from "./screens/HowTo";
import type { ToastState } from "./components/overlays";
import { saveFile } from "./download";
import { Assistant } from "./screens/Assistant";
import type { ScopeOption, Turn } from "./screens/Assistant";
import { Compare } from "./screens/Compare";
import type { CompareMode } from "./screens/Compare";
import { DocumentDetail } from "./screens/DocumentDetail";
import { Documents } from "./screens/Documents";

const HEALTH_POLL_MS = 20_000;

/** Prefer the server's explanation of a failure; it says what to fix. */
function describe(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Something went wrong.";
}

export default function App() {
  const [screen, setScreen] = useState<ScreenId>("documents");
  const [openId, setOpenId] = useState<string | null>(null);

  const [documents, setDocuments] = useState<DocumentSummary[]>([]);
  const [details, setDetails] = useState<Record<string, Detail>>({});
  const [health, setHealth] = useState<Health | null>(null);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<DocumentSummary[] | null>(null);
  const [searching, setSearching] = useState(false);

  // Comparison is its own screen, reachable from the side navigation whether or not
  // anything is selected - it explains how to select when nothing is. The selection
  // outlives a visit, so leaving and coming back finds the rows still ticked.
  const [selected, setSelected] = useState<string[]>([]);
  const [onlyDifferences, setOnlyDifferences] = useState(true);

  const [compareQuestion, setCompareQuestion] = useState("");
  const [compareAnswer, setCompareAnswer] = useState<string | null>(null);
  const [compareAsking, setCompareAsking] = useState(false);
  const [compareError, setCompareError] = useState<string | null>(null);

  // Which comparison the Compare screen is showing. Visual needs no state of its
  // own beyond this: it draws the archived pages the details cache already holds,
  // so there is nothing to fetch, nothing to ask for and nothing to reset.
  const [compareMode, setCompareMode] = useState<CompareMode>("values");

  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState("");
  const [thinking, setThinking] = useState(false);
  /**
   * What the assistant answers about: the open document, the ticked rows, or the
   * whole library.
   *
   * The demo's most confusing moment was that this was never stated. Opening one
   * drawing and asking about its top view produced an answer that also named
   * three other documents, because the assistant had always searched the library
   * and nothing on screen said so. It is now a choice with a default that follows
   * what the user is looking at, and the choice is shown above the composer.
   */
  const [chatScope, setChatScope] = useState<string>("library");
  // The assistant has two homes: its own screen, and a panel docked to the right
  // of whichever screen is open. Both render the same conversation from the state
  // above, so moving between them costs nothing and loses nothing. They are never
  // shown at once - two live copies of one thread, each scrolling independently,
  // would only be confusing.
  // Open by default. The assistant is the thing this demo is for, and a visitor
  // who has to find a button before anything can be asked has already been shown
  // a document viewer instead.
  // Only where it docks beside the screen, though. At 1100px and below the panel
  // slides over the content (full-screen on a phone), so opening it by default
  // there means the first thing a visitor sees is an empty chat instead of the
  // drawings. 1101px matches the panel's breakpoint in index.css.
  const [assistantDocked, setAssistantDocked] = useState(
    () => window.matchMedia("(min-width: 1101px)").matches,
  );

  const [exporting, setExporting] = useState(false);

  const [toast, setToast] = useState<ToastState | null>(null);

  // Read once, on the first render, rather than in an effect: an overlay that
  // appears a frame after the app does reads as something that went wrong.
  const [welcome, setWelcome] = useState(() => shouldShowWelcome());

  // Every visit restarts the thirty days, whether or not the overlay opened.
  // Recorded after the first render has already decided, so it cannot hide the
  // overlay it is meant to time.
  useEffect(() => markVisit(), []);

  const notify = useCallback((title: string, caption?: string, tone: ToastState["tone"] = "success") => {
    setToast({ title, caption, tone });
  }, []);

  // ------------------------------------------------------------ server state
  const refreshDocuments = useCallback(async () => {
    try {
      setDocuments(await api.listDocuments());
    } catch (error) {
      notify("Could not load the library", describe(error), "danger");
    }
  }, [notify]);

  useEffect(() => {
    void refreshDocuments();
  }, [refreshDocuments]);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const next = await api.health();
        if (!cancelled) setHealth(next);
      } catch {
        // The status bar shows "unreachable" from the missing health object;
        // a failed poll needs no separate error of its own.
        if (!cancelled) setHealth(null);
      }
    };
    void poll();
    const timer = window.setInterval(poll, HEALTH_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  /** Fetch a document's full extraction once and remember it. */
  const loadDetail = useCallback(
    async (id: string): Promise<Detail | null> => {
      if (details[id]) return details[id];
      try {
        const detail = await api.getDocument(id);
        setDetails((current) => ({ ...current, [id]: detail }));
        return detail;
      } catch (error) {
        notify("Could not open the document", describe(error), "danger");
        return null;
      }
    },
    [details, notify],
  );

  const openDocument = useCallback(
    (id: string) => {
      setOpenId(id);
      // Opening a drawing is also the clearest statement of what you want to talk
      // about, so the assistant follows you into it. The selector above the
      // composer says so and can be set back to the library in one click - what
      // it may not do is stay silently pointed at all seven documents while you
      // are looking at one.
      setChatScope("document");
      // Also what leaves the comparison: its column headings link to exactly this,
      // and a detail view behind the compare screen would be unreachable.
      setScreen("documents");
      void loadDetail(id);
    },
    [loadDetail],
  );

  // ------------------------------------------------------------------ search
  const runSearch = useCallback(async () => {
    const text = query.trim();
    if (!text) return;
    setSearching(true);
    try {
      setResults(await api.search(text));
    } catch (error) {
      notify("Search failed", describe(error), "danger");
    } finally {
      setSearching(false);
    }
  }, [query, notify]);

  const clearSearch = useCallback(() => {
    setResults(null);
    setQuery("");
  }, []);

  // -------------------------------------------------- selection and comparison
  const toggleRow = useCallback((id: string) => {
    setSelected((current) =>
      current.includes(id) ? current.filter((each) => each !== id) : [...current, id],
    );
  }, []);

  /** The grid's header checkbox: select every visible row, or clear them if all are already ticked. */
  const toggleAll = useCallback((ids: string[]) => {
    setSelected((current) => {
      const all = ids.length > 0 && ids.every((id) => current.includes(id));
      if (all) return current.filter((id) => !ids.includes(id));
      const next = new Set(current);
      for (const id of ids) next.add(id);
      return [...next];
    });
  }, []);

  const clearSelection = useCallback(() => setSelected([]), []);

  const startCompare = useCallback(() => {
    setOpenId(null);
    setScreen("compare");
  }, []);

  // Comparing needs the full extraction of every selected row, but the grid only
  // ever loaded summaries. Fetch the missing ones on entering the mode; the ref
  // keeps two renders from requesting the same document twice, and forgets a
  // failed request so it can be retried.
  const requestedForCompare = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (screen !== "compare") return;
    for (const id of selected) {
      if (details[id] || requestedForCompare.current.has(id)) continue;
      requestedForCompare.current.add(id);
      void loadDetail(id).then((detail) => {
        if (!detail) requestedForCompare.current.delete(id);
      });
    }
  }, [screen, selected, details, loadDetail]);

  // An answer is about the documents that were selected when it was asked for, so
  // changing the selection retires it rather than leaving prose on screen that
  // describes a set the user is no longer looking at. The question survives: it is
  // the user's own words and is usually still what they want to ask.
  useEffect(() => {
    setCompareAnswer(null);
    setCompareError(null);
  }, [selected]);

  const askCompare = useCallback(async () => {
    if (selected.length < 2) return;
    setCompareAsking(true);
    setCompareError(null);
    try {
      const response = await api.compareDocuments(selected, compareQuestion.trim());
      setCompareAnswer(response.answer);
    } catch (error) {
      setCompareError(describe(error));
    } finally {
      setCompareAsking(false);
    }
  }, [selected, compareQuestion]);

  // ------------------------------------------------------------------- export
  /**
   * Download extracted data as CSV: one document, or a whole ticked selection.
   *
   * The file is built by the server rather than from the summaries this component
   * holds, for the same reason search is: what a document contains is
   * prompt-defined, and the backend is where a value is already turned into the
   * label and text it is shown and indexed under. A selection becomes one file,
   * since the rows name the document they came from.
   */
  const exportCsv = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0 || exporting) return;
      setExporting(true);
      try {
        const file =
          ids.length === 1
            ? await api.exportDocumentCsv(ids[0])
            : await api.exportDocumentsCsv(ids);
        saveFile(file);
        notify(
          ids.length === 1 ? "Exported to CSV" : `${ids.length} documents exported to CSV`,
          file.filename,
        );
      } catch (error) {
        notify(
          ids.length === 1 ? "Could not export the document" : "Could not export the documents",
          describe(error),
          "danger",
        );
      } finally {
        setExporting(false);
      }
    },
    [exporting, notify],
  );

  // ------------------------------------------------------- assistant scope
  /**
   * The scopes on offer, in the order they are listed.
   *
   * Only what exists is offered: there is no "this drawing" entry with nothing
   * open, and no "selected" entry with nothing ticked, because an option that
   * cannot be honoured is the same confusion in a different place. The library
   * is always last and always available - it is the way back out of a pin.
   */
  const scopeOptions = useMemo<ScopeOption[]>(() => {
    const openName = openId
      ? (documents.find((row) => row.id === openId)?.name ?? "this drawing")
      : null;
    const options: ScopeOption[] = [];
    if (openId) {
      options.push({ id: "document", label: "This drawing", caption: openName ?? "this drawing" });
    }
    if (selected.length > 0) {
      options.push({
        id: "selection",
        label: `Selected (${selected.length})`,
        caption: selected
          .map((id) => documents.find((row) => row.id === id)?.name ?? id)
          .join(", "),
      });
    }
    options.push({
      id: "library",
      label: "Whole library",
      caption: `all ${documents.length} ${documents.length === 1 ? "document" : "documents"}`,
    });
    return options;
  }, [documents, openId, selected]);

  /**
   * The scope a question is actually sent under.
   *
   * Resolved from the options rather than from the raw state, so a scope whose
   * subject has gone - the document was closed, the selection cleared - falls
   * back to the library instead of pinning the answer to nothing. The eight the
   * route accepts are the eight that are sent: a longer selection is not a scope
   * anyone could read an answer against, and this library holds seven.
   */
  const activeScope = useMemo(() => {
    const chosen = scopeOptions.find((option) => option.id === chatScope) ?? scopeOptions.at(-1)!;
    const ids =
      chosen.id === "document" && openId
        ? [openId]
        : chosen.id === "selection"
          ? selected.slice(0, 8)
          : [];
    return { id: chosen.id, caption: chosen.caption, documentIds: ids };
  }, [chatScope, openId, scopeOptions, selected]);

  // --------------------------------------------------------------------- chat
  const send = useCallback(async () => {
    const question = draft.trim();
    if (!question || thinking) return;

    const history = turns.map(({ role, content }) => ({ role, content }));
    // Snapshotted with the question: the scope can be changed while an answer is
    // in flight, and the label under the reply has to describe what that reply
    // was actually answered from.
    const asked = activeScope;
    setTurns((current) => [...current, { role: "user", content: question }]);
    setDraft("");
    setThinking(true);

    try {
      const response = await api.chat(question, history, asked.documentIds);
      setTurns((current) => [
        ...current,
        {
          role: "assistant",
          content: response.answer,
          sources: response.sources,
          scope: response.scope,
          scopeLabel: asked.caption,
        },
      ]);
    } catch (error) {
      setTurns((current) => [
        ...current,
        { role: "assistant", content: `That question could not be answered: ${describe(error)}` },
      ]);
    } finally {
      setThinking(false);
    }
  }, [draft, thinking, turns, activeScope]);

  /** Show the assistant beside the current screen. */
  const dockAssistant = useCallback(() => {
    setAssistantDocked(true);
    // Leaving the assistant screen docked would show the same thread twice, so
    // the screen behind it goes back to the library.
    setScreen((current) => (current === "assistant" ? "documents" : current));
  }, []);

  /** Give the assistant the whole content column instead. */
  const expandAssistant = useCallback(() => {
    setAssistantDocked(false);
    setScreen("assistant");
  }, []);

  // ------------------------------------------------------------------ derived
  const navItems = useMemo<NavItem[]>(
    () => [
      { id: "documents", label: "Documents", icon: "description", badge: documents.length },
      // The badge counts what is ticked, so the tab doubles as the reminder that a
      // selection is waiting - and reads 2 when the comparison is ready to run.
      { id: "compare", label: "Compare", icon: "compare_arrows", badge: selected.length },
      { id: "assistant", label: "Assistant", icon: "forum" },
    ],
    [documents.length, selected.length],
  );

  // No "test on your files" entry: that is what the band across the top is for,
  // and the same call to action twice in one screen halves the weight of both.
  const aboutItems = useMemo<NavItem[]>(
    () => [
      { id: "howto", label: "How to use it?", icon: "play_circle" },
      { id: "feedback", label: "Leave feedback", icon: "rate_review" },
      { id: "contact", label: "Contact us", icon: "mail" },
    ],
    [],
  );

  const dismissWelcome = useCallback(() => {
    setWelcome(false);
  }, []);

  // Escape closes it, the same as every other dialog in the app. A modal that
  // ignores Escape is the first thing a keyboard user notices.
  useEffect(() => {
    if (!welcome) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") dismissWelcome();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [welcome, dismissWelcome]);

  // The side navigation as a drawer, on phones only - everywhere wider it is
  // always on screen and this stays false.
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setNavOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navOpen]);

  /** Leave the workspace for one of the pages about the demo. */
  const openPage = useCallback((page: ScreenId) => {
    setScreen(page);
    setOpenId(null);
    setAssistantDocked(false);
  }, []);

  const openDetail = openId ? details[openId] : undefined;


  // Selection order decides column order, so map over `selected` rather than over
  // the details cache. Rows still loading are simply absent, which is what
  // `compareLoading` reports.
  const compareDocuments = useMemo(
    () => selected.map((id) => details[id]).filter((detail): detail is Detail => Boolean(detail)),
    [selected, details],
  );
  const compareLoading = compareDocuments.length < selected.length;

  return (
    <div className="demo-page">
      <PromoBar documents={documents.length} onContact={() => openPage("contact")} />
      <div className="dac-app app-shell">
      <TopBar
        // The top bar's button is the panel: it is the control that sits above
        // every screen, so it opens the assistant without taking you off the one
        // you are on. The side navigation's Assistant entry is the full screen.
        onToggleAssistant={() => (assistantDocked ? setAssistantDocked(false) : dockAssistant())}
        assistantOpen={assistantDocked}
        onToggleNav={() => setNavOpen((current) => !current)}
        navOpen={navOpen}
      />

      <SideNav
        items={navItems}
        about={aboutItems}
        value={screen}
        open={navOpen}
        onChange={(next) => {
          setNavOpen(false);
          setScreen(next);
          if (next === "documents") setOpenId(null);
          // The full screen and the panel are the same conversation, so opening
          // one puts the other away.
          if (next === "assistant") setAssistantDocked(false);
        }}
      />
      {navOpen && <div className="nav-scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />}

      <main className="app-main">
        {screen === "documents" &&
          (openDetail ? (
            <DocumentDetail
              document={openDetail}
              onAsk={() => {
                setChatScope("document");
                dockAssistant();
              }}
              asking={assistantDocked && activeScope.id === "document"}
              onBack={() => setOpenId(null)}
              onExport={() => void exportCsv([openDetail.id])}
              exporting={exporting}
            />
          ) : openId ? (
            <section className="screen">
              <div className="screen__body">
                <div className="empty">
                  <div className="empty__card">Loading document...</div>
                </div>
              </div>
            </section>
          ) : (
            <Documents
              documents={documents}
              results={results}
              query={query}
              searching={searching}
              selected={selected}
              onQueryChange={setQuery}
              onSearch={() => void runSearch()}
              onClearSearch={clearSearch}
              onOpen={openDocument}
              onToggleRow={toggleRow}
              onToggleAll={toggleAll}
              onClearSelection={clearSelection}
              onCompare={startCompare}
              exporting={exporting}
              onExportSelected={() => void exportCsv(selected)}
            />
          ))}

        {screen === "compare" && (
          <Compare
            documents={compareDocuments}
            loading={compareLoading}
            onlyDifferences={onlyDifferences}
            onToggleOnlyDifferences={setOnlyDifferences}
            onOpen={openDocument}
            onBack={() => setScreen("documents")}
            library={documents}
            selected={selected}
            onToggle={toggleRow}
            question={compareQuestion}
            answer={compareAnswer}
            asking={compareAsking}
            askError={compareError}
            onQuestionChange={setCompareQuestion}
            onAsk={() => void askCompare()}
            mode={compareMode}
            onModeChange={setCompareMode}
          />
        )}

        {screen === "howto" && <HowTo onContact={() => openPage("contact")} />}

        {screen === "feedback" && <Feedback />}

        {screen === "contact" && <Contact />}

        {screen === "assistant" && (
          <Assistant
            turns={turns}
            draft={draft}
            thinking={thinking}
            onDraftChange={setDraft}
            onSend={() => void send()}
            onOpenDocument={openDocument}
            libraryCount={documents.length}
            scope={activeScope.id}
            scopeOptions={scopeOptions}
            onScopeChange={setChatScope}
            onBack={() => setScreen("documents")}
            actions={
              <Button size="sm" icon="dock_to_right" onClick={dockAssistant}>
                Dock to the right
              </Button>
            }
          />
        )}
      </main>

      {assistantDocked && (
        <Assistant
          variant="panel"
          turns={turns}
          draft={draft}
          thinking={thinking}
          onDraftChange={setDraft}
          onSend={() => void send()}
          // Opening a source from the panel navigates the screen beside it and
          // leaves the conversation up, which is the point of docking.
          onOpenDocument={openDocument}
          libraryCount={documents.length}
          scope={activeScope.id}
          scopeOptions={scopeOptions}
          onScopeChange={setChatScope}
          actions={
            <>
              <IconButton
                icon="open_in_full"
                label="Open as a full screen"
                size="sm"
                onClick={expandAssistant}
              />
              <IconButton
                icon="close"
                label="Close the assistant"
                size="sm"
                onClick={() => setAssistantDocked(false)}
              />
            </>
          }
        />
      )}

      <StatusBar health={health} />

      {toast && <Toast toast={toast} onDismiss={() => setToast(null)} />}
      </div>

      {/*
        Shown on a first visit and after thirty days away - see `shouldShowWelcome`. It
        is skipped entirely while no video is configured, so this renders nothing
        at all until one is.
      */}
      {welcome && (
        <div className="welcome" role="dialog" aria-modal="true" aria-label="Welcome">
          <div className="welcome__card">
            <div className="welcome__head">
              <div>
                <div className="eyebrow">Technical Drawings Reader</div>
                <div className="welcome__title">Turn drawings into structured data</div>
              </div>
              <IconButton icon="close" label="Close" onClick={dismissWelcome} />
            </div>
            <VideoFrame autoplay />
            <div className="welcome__foot">
              <span className="screen__caption">
                You can reopen this any time from <strong>How to use it?</strong> in the menu.
              </span>
              <span className="u-spacer" />
              <Button variant="primary" onClick={dismissWelcome}>
                Start exploring
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
